import { create } from 'zustand'
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  type User,
} from 'firebase/auth'
import { auth, getAdminEmails, isFirebaseConfigured } from '@/lib/firebase'

interface AuthState {
  user: User | null
  loading: boolean
  initialized: boolean
  isAdmin: boolean
  login: (email: string, password: string) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  logout: () => Promise<void>
  initAuth: () => () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  initialized: false,
  isAdmin: false,

  login: async (email, password) => {
    set({ loading: true })
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      const adminEmails = getAdminEmails()
      const emailLower = cred.user.email?.toLowerCase() || ''
      const isAdmin =
        adminEmails.length === 0 || adminEmails.includes(emailLower)
      if (!isAdmin) {
        await signOut(auth)
        throw new Error('Access denied. Admin privileges required.')
      }
      set({ user: cred.user, isAdmin: true })
    } finally {
      set({ loading: false })
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    const user = auth.currentUser
    if (!user?.email) {
      throw new Error('You must be signed in to change your password.')
    }
    set({ loading: true })
    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      )
      await reauthenticateWithCredential(user, credential)
      await updatePassword(user, newPassword)
    } finally {
      set({ loading: false })
    }
  },

  logout: async () => {
    if (isFirebaseConfigured()) {
      await signOut(auth)
    }
    set({ user: null, isAdmin: false })
  },

  initAuth: () => {
    if (!isFirebaseConfigured()) {
      set({ initialized: true, loading: false, isAdmin: false, user: null })
      return () => {}
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const adminEmails = getAdminEmails()
      const emailLower = user?.email?.toLowerCase() || ''
      const isAdmin =
        !user ||
        adminEmails.length === 0 ||
        adminEmails.includes(emailLower)
      set({
        user,
        isAdmin: Boolean(user && isAdmin),
        initialized: true,
        loading: false,
      })
    })
    return unsubscribe
  },
}))
